import { ConstraintDocument, ConstraintMode } from 'types/ConstraintProfileTypes';
import { diffDocumentPaths, normalizeRebalancingRiskForDiff, VarLimitsByPlan } from 'utils/constraintDocument';
import { panelSectionForGroup } from '../../panelSections';

/**
 * Per-section count of fields a profile differs from the mode's schema defaults.
 * Returns `{ panelSectionKey: count }` keyed by VINCOLI-PANEL section so each
 * row maps 1:1 to an accordion in the Vincoli pane — e.g. the "asset"
 * ("Strumenti") section rolls up changes in 4 real groups (asset,
 * instrument_type, core_satellite, investment_threshold) into a single count
 * (spec §7.5).
 *
 * Only counts paths that the PROFILE actually contains. A profile is the
 * source of truth for "what the user explicitly saved", so:
 *
 *   - Groups absent from the profile (e.g. builder profiles legitimately omit
 *     `order`/`liquidity`, and `product` is never part of the savable schema)
 *     must NOT be reported even when predefiniti has them — they're
 *     mode-irrelevant / non-savable, not "differences from defaults".
 *
 *   - Fields absent from a present group (e.g. `risk_limits` lives in
 *     predefiniti but not in profile.risk) must NOT be reported either —
 *     they're reference data, not user-editable constraints.
 */
export function computeDeltaCounts(
  profileConstraints: ConstraintDocument,
  schemaDefaults: ConstraintDocument,
  mode: ConstraintMode,
): Record<string, number> {
  // In rebalancing, normalize the off-state expressions for align_to_plan
  // and target_var_min/max so that profiles saved with explicit nulls
  // don't show false "delta vs defaults" indicators — both null and the
  // plan's verde min/max mean "no advisor override". The plan-keyed VaR
  // limits ride inside `schemaDefaults.risk.risk_limits` (predefiniti
  // always carries them).
  const limits =
    mode === 'rebalancing'
      ? (
          (schemaDefaults?.risk as Record<string, unknown> | undefined)
            ?.risk_limits as VarLimitsByPlan | undefined
        )
      : undefined;
  const left = limits
    ? normalizeRebalancingRiskForDiff(profileConstraints, limits)
    : profileConstraints;
  const right = limits
    ? normalizeRebalancingRiskForDiff(schemaDefaults, limits)
    : schemaDefaults;

  const counts: Record<string, number> = {};
  for (const path of diffDocumentPaths(left, right, mode)) {
    const isFieldPath = path.includes('.');
    const group = isFieldPath ? path.slice(0, path.indexOf('.')) : path;
    const field = isFieldPath ? path.slice(path.indexOf('.') + 1) : null;

    const profileGroup = profileConstraints?.[group];
    if (profileGroup == null) continue;

    if (field !== null) {
      const profileGroupObj = profileGroup as Record<string, unknown>;
      if (!(field in profileGroupObj)) continue;
    }

    const section = panelSectionForGroup(group);
    counts[section] = (counts[section] ?? 0) + 1;
  }
  return counts;
}
